/**
 * Cross-platform download / share helper.
 *
 * Web: creates an object URL and triggers a synthetic anchor click — the
 *      existing behaviour, unchanged.
 * Native (Capacitor): writes the bytes to the app's Cache directory via
 *      @capacitor/filesystem then opens the system Share sheet via
 *      @capacitor/share so the user can Save to Files / send to another
 *      app. iOS WKWebView silently drops `<a download>` clicks, so this is
 *      the only reliable path to actually save a file on native.
 *
 * saveOrShareBlob(blob, filename, options?)
 *   - blob: a Blob (PDF, CSV, ZIP)
 *   - filename: sensible display filename e.g. "INV-00123.pdf"
 *   - options.title, options.dialogTitle for the native Share sheet
 *
 * Returns:  { path: <native cache path or null>, shared: <bool> }
 * Throws:   only if BOTH save and share fail. Web path never throws.
 */
import { isNativeApp } from "@/lib/platform";

const b64 = (bytes) => {
  // Encode a Uint8Array to base64 without blowing the JS string limit for
  // large PDFs / ZIPs. Chunked to 32 KiB per pass.
  const CHUNK = 0x8000;
  let out = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(out);
};

async function _webSave(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to actually start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return { path: null, shared: false };
}

async function _nativeSave(blob, filename, options = {}) {
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);

  const buf = await blob.arrayBuffer();
  const data = b64(new Uint8Array(buf));

  // Cache dir is transient — perfect for share-and-forget files. We never
  // touch the user's Documents/ or Photos/ without their explicit action.
  const written = await Filesystem.writeFile({
    path: filename,
    data,
    directory: Directory.Cache,
  });

  try {
    await Share.share({
      title: options.title || filename,
      text: options.text || "",
      url: written.uri,
      dialogTitle: options.dialogTitle || "Save or share",
    });
    return { path: written.uri, shared: true };
  } catch (shareErr) {
    // User cancelled the share sheet — the file still exists in Cache and
    // is discoverable via Files.app / native file picker.
    if (String(shareErr?.message || "").toLowerCase().includes("cancel")) {
      return { path: written.uri, shared: false };
    }
    throw shareErr;
  }
}

/**
 * Save (web) or share (native) an arbitrary blob under a display filename.
 *
 * Callers should always await this and wrap with try/catch to surface a
 * user-visible toast on hard failure.
 */
export async function saveOrShareBlob(blob, filename, options = {}) {
  if (!blob) throw new Error("Nothing to download");
  if (isNativeApp()) {
    try {
      return await _nativeSave(blob, filename, options);
    } catch (err) {
      // If Filesystem/Share plugin isn't packaged (e.g. running an older
      // build), degrade to the web anchor path so the user still gets *something*.
      // In practice this only fires if `npx cap sync` was skipped.
      // eslint-disable-next-line no-console
      console.warn("[downloads] native save failed, falling back to web anchor:", err);
      return _webSave(blob, filename);
    }
  }
  return _webSave(blob, filename);
}

/** Convenience: fetch an authenticated URL (with Bearer) and save the
 *  response blob. Filename can be overridden; if omitted, uses the
 *  server-supplied Content-Disposition. */
export async function fetchAndSave(url, { token, filename, method = "GET", body, headers = {}, options = {} } = {}) {
  const res = await fetch(url, {
    method,
    body,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...headers },
  });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try { const j = await res.json(); if (j?.detail) msg = j.detail; } catch (_) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  let name = filename;
  if (!name) {
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    name = m ? m[1] : `download-${Date.now()}`;
  }
  return saveOrShareBlob(blob, name, options);
}
